"use client";
import { useEffect, useState, memo, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@/components/ui/button";
import * as LucideIcons from "lucide-react";
import { setProjects, setActiveItem, setUserData } from "@/store/mainSlice";
import { handleView, handleDownload, downloadCardFromURL, downloadPDF } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import BuyCreditsModal from "./BuyCreditsModal";
import { ShareLinkPopup } from "./share-link-popup";
import { setPublicWithChats, setIsChatLoading } from "@/store/mainSlice";
import {
    MoreHorizontalIcon,
    ShareIcon,
    TrashIcon,
    DownloadIcon,
    EyeIcon,
    PencilEditIcon,
} from "./icons";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
const capitalizeTitle = (text) => {
    return text
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};
import UpgradePlan from "./upgrade-plan";

export default function CategoryMoreMenu({ publicProjects, isCollapsed }) {
    const router = useRouter();
    const inputRef = useRef(null);
    const dispatch = useDispatch();
    const pathname = usePathname();
    const projects = useSelector((state) => state.main.projects || []);
    const publicWithChats = useSelector(
        state => state.main.publicWithChats || []
    );
    const { activeTab, isFreeLimit, userData, isGeneratingWebsite } = useSelector((state) => state.main);
    const userId1 = userData?._id;
    const [categoryChats, setCategoryChats] = useState({});
    const [openCategory, setOpenCategory] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [openMenuFor, setOpenMenuFor] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [newName, setNewName] = useState("");
    const { setOpenMobile } = useSidebar();
    const [renamingChatId, setRenamingChatId] = useState(null);
    const [newChatName, setNewChatName] = useState("");
    const [hoveredChatId, setHoveredChatId] = useState(null);
    const chatInputRef = useRef(null);
    const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [generateLinkData, setGenerateLinkData] = useState(null);
    const [showPlan, setShowPlan] = useState(false);
    const effectiveTab = activeTab ?? "Chat";
    let chatType = "chat";
    if (effectiveTab === "Card Maker") {
        chatType = "card_maker";
    } else if (effectiveTab === "Page Builder") {
        chatType = "page_builder";
    } else if (effectiveTab === "Go-to-Market Strategy") {
        chatType = "go_to_market_strategy";
    } else {
        chatType = effectiveTab.toLowerCase().replace(/[\s\/]+/g, "_");
    }
    const subscriptionStatus = userData?.subscriptionStatus;
    const groupChatsByDate = (history = []) => {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const toValidDate = (value) => {
            if (!value) return null;
            if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : value;
            }
            if (typeof value === "number") {
                const d = new Date(value);
                return Number.isNaN(d.getTime()) ? null : d;
            }
            if (typeof value === "string") {
                const s = value.trim();
                if (!s) return null;
                const d = /^\d+$/.test(s) ? new Date(Number(s)) : new Date(s);
                return Number.isNaN(d.getTime()) ? null : d;
            }
            return null;
        };

        const getItemDate = (item) => {
            const candidates = [
                item?.updatedAt,
                item?.updated_at,
                item?.lastMessageAt,
                item?.last_message_at,
                item?.createdAt,
                item?.created_at,
                item?.timestamp,
            ];
            for (const c of candidates) {
                const d = toValidDate(c);
                if (d) return d;
            }
            return new Date();
        };

        const startOfDayMs = (date) =>
            new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

        const formatDate = (date) =>
            date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });

        const labelForDate = (d) => {
            if (d.toDateString() === today.toDateString()) return "Today";
            if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
            return formatDate(d);
        };

        const sorted = [...(history || [])].sort(
            (a, b) => getItemDate(b).getTime() - getItemDate(a).getTime()
        );

        const groupsByDay = new Map();
        for (const item of sorted) {
            const d = getItemDate(item);
            const dayKey = startOfDayMs(d);
            if (!groupsByDay.has(dayKey)) {
                groupsByDay.set(dayKey, { key: dayKey, label: labelForDate(d), chats: [] });
            }
            groupsByDay.get(dayKey).chats.push(item);
        }

        return Array.from(groupsByDay.values()).sort((a, b) => b.key - a.key);
    };
    const checkSubscriptionAndProceed = (action) => {
        // Check both Redux state and localStorage for subscription status
        const storedStatus = localStorage.getItem("subscriptionStatus");
        if (subscriptionStatus !== "active" && storedStatus !== "active") {
            setShowPlan(true); // 👈 open upgrade popup
            return;
        }

        action(); // 👈 allowed action
    };

    // Listen for subscription status updates
    useEffect(() => {
        const handleSubscriptionUpdate = (e) => {
            const { status, userId } = e.detail;
            // Only update if it's for the current user
            if (status === "active" && (!userId || userId === userData?._id)) {
                // Update Redux userData
                if (userData?._id) {
                    dispatch(setUserData({
                        ...userData,
                        subscriptionStatus: "active"
                    }));
                }
            }
        };

        window.addEventListener("subscription-updated", handleSubscriptionUpdate);
        return () => {
            window.removeEventListener("subscription-updated", handleSubscriptionUpdate);
        };
    }, [userData, dispatch]);

    useEffect(() => {
        const handler = (e) => {
            const { project } = e.detail;
            setOpenCategory(project.parent_scr_id);

            router.push(`/project/${project.parent_scr_id}`);
        };

        window.addEventListener("project-created", handler);
        return () =>
            window.removeEventListener("project-created", handler);
    }, []);

    useEffect(() => {
        if (!publicProjects?.length || !userId1) return;
        publicProjects.forEach(async (pub) => {
            // don't refetch if already fetched
            if (categoryChats[pub.parent_scr_id]) return;

            try {
                const res = await fetch(
                    `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/chats/${pub.parent_scr_id}/${chatType}/${userId1}`
                );

                const data = await res.json();
                if (data.status === 1 && data.chats?.length > 0) {
                    setCategoryChats((prev) => ({
                        ...prev,
                        [pub.parent_scr_id]: data.chats
                    }));
                    dispatch(setPublicWithChats([
                        ...new Set([
                            ...publicWithChats,
                            pub.parent_scr_id
                        ])
                    ]));
                }
            } catch (err) {
                console.error("Public chat fetch error:", err);
            }
        });
    }, [publicProjects, chatType, userId1, publicWithChats]);

    useEffect(() => {
        setCategoryChats({});
        projects.forEach(project => {
            fetchChats(project.parent_scr_id);
        });
    }, [activeTab]);


    const fetchChats = async (parentScrId) => {
        try {
            const res = await fetch(
                `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/chats/${parentScrId}/${chatType}/${userId1}`
            );
            const data = await res.json();
            console.log(data, "---data--fetchChats")
            if (data.status === 1) {
                setCategoryChats((prev) => ({
                    ...prev,
                    [parentScrId]: data.chats,
                }));
            }
        } catch (error) {
            console.log("Error fetching chats:", error);
        }
    };

    useEffect(() => {
        setShareOpen(true);
    }, [generateLinkData]);

    const handleIconClick = async (parentScrId) => {
        if (openCategory === parentScrId) {
            setOpenCategory(null);
        } else {
            setOpenCategory(parentScrId);
            if (!categoryChats[parentScrId]) {
                await fetchChats(parentScrId);
            }
        }
    };

    const handleDeleteProject = async (parent_scr_id) => {
        if (!parent_scr_id) return;

        try {
            const res = await fetch(
                `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/delete/${parent_scr_id}`,
                {
                    method: "DELETE",
                }
            );

            const data = await res.json();

            if (data.status === 1) {
                toast.success("Project deleted successfully", {
                    style: { color: "green" },
                    position: "top-center",
                    iconTheme: { primary: "green", secondary: "#fff" },
                });

                // dispatch(
                //     setProjects(
                //         projects.filter((p) => p.parent_scr_id !== parent_scr_id)
                //     )
                // );
                localStorage.removeItem("parent_scr_id");
                localStorage.removeItem("projectName");
                dispatch(setProjects((prev) =>
                    Array.isArray(prev) ? prev.filter(p => p.parent_scr_id !== parent_scr_id) : []
                ));
                window.dispatchEvent(new Event("refresh-projects"));

                router.push("/");
            } else {
                toast.error(data.message || "Failed to delete project", {
                    style: { color: "red" },
                    iconTheme: { primary: "red", secondary: "#fff" },
                    position: "top-center",
                });
            }
        } catch (err) {
            console.error("Delete error:", err);
            toast.error("Something went wrong while deleting", {
                style: { color: "red" },
                position: "top-center",
            });
        }
    };

    const handleRenameChat = async () => {
        if (!newChatName.trim()) return setRenamingChatId(null);

        try {
            const res = await fetch(
                `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/rename-chat/${renamingChatId}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ title: newChatName }),
                }
            );

            const data = await res.json();
            if (data.status !== 1) {
                toast.error(data.message || "Failed to rename chat");
                return;
            }

            toast.success("Chat renamed successfully", {
                style: { color: "green" },
                position: "top-center",
            });

            // Update UI immediately
            setCategoryChats((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((key) => {
                    updated[key] = updated[key].map((c) =>
                        c.screen_id === renamingChatId
                            ? { ...c, title: newChatName }
                            : c
                    );
                });
                return updated;
            });

            setRenamingChatId(null);
        } catch (err) {
            console.error("Rename chat error:", err);
            toast.error("Something went wrong while renaming chat");
        }
    };

    const handleChatRenameKey = (e) => {
        if (e.key === "Enter") handleRenameChat();
        if (e.key === "Escape") setRenamingChatId(null);
    };

    useEffect(() => {
        if (renamingChatId && chatInputRef.current) {
            const el = chatInputRef.current;
            el.focus();

            // Select all text inside contentEditable
            const range = document.createRange();
            range.selectNodeContents(el);

            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }, [renamingChatId]);

    //   useEffect(() => {
    //   const status = localStorage.getItem("subscriptionStatus");
    //   if (status === "active") {
    //     setShowPlan(false);
    //   }
    // }, []);

    const handleRenameProject = async () => {
        if (!newName.trim()) return setRenamingId(null);
        try {
            const res = await fetch(
                `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/rename/${renamingId}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ newName }),
                }
            );
            const data = await res.json();
            if (data.status !== 1) {
                toast.error(data.message || "Failed to rename project");
                return;
            }
            toast.success("Project renamed successfully", {
                style: { color: "green" },
                position: "top-center",
                iconTheme: { primary: "green", secondary: "#fff" },
            });
            dispatch(
                setProjects(
                    projects.map((p) =>
                        p.parent_scr_id === renamingId
                            ? { ...p, category: data.project.category }
                            : p
                    )
                )
            );

            setRenamingId(null);
        } catch (err) {
            console.error("Rename error:", err);
            toast.error("Something went wrong while renaming");
        }
    };

    const handleRenameKey = (e) => {
        if (e.key === "Enter") handleRenameProject();
        if (e.key === "Escape") setRenamingId(null);
    };

    useEffect(() => {
        if (renamingId && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
                inputRef.current.select();
            }, 150);
        }
    }, [renamingId]);

    useEffect(() => {
        const segments = pathname.split("/");
        if (segments[1] === "project" && segments[2]) {
            const currentProjectId = segments[2];
            setOpenCategory(currentProjectId);
        }
    }, [pathname, projects]);

    useEffect(() => {
        const handler = (e) => {
            const { parentScrId, screenId, title } = e.detail || {};
            if (!parentScrId || !screenId) return;

            setCategoryChats(prev => {
                const existing = prev[parentScrId] || [];

                // prevent duplicate
                if (existing.some(c => c.screen_id === screenId)) return prev;

                return {
                    ...prev,
                    [parentScrId]: [
                        ...existing,
                        {
                            screen_id: screenId,
                            title: title
                        }
                    ]
                };
            });
        };

        window.addEventListener("chat-added", handler);
        window.addEventListener("public-chat-updated", handler);
        return () => {
            window.removeEventListener("chat-added", handler);
            window.removeEventListener("public-chat-updated", handler);
        };
    }, [activeTab]);

    //     const sidebarProjects = [
    //         ...(Array.isArray(projects) ? projects : []),
    //         ...(publicProjects || [])
    //             .filter(pub => {
    //                 // const hasChats = categoryChats[pub.parent_scr_id]?.length > 0;

    //                 const existsInPrivate = projects.some(
    //                     p => p.parent_scr_id === pub.parent_scr_id
    //                 );
    //  return publicWithChats.includes(pub.parent_scr_id) && !existsInPrivate;
    //                 // return hasChats && !existsInPrivate;
    //             })
    //             .map(pub => ({
    //                 parent_scr_id: pub.parent_scr_id,
    //                 category: pub.category,
    //                 icon: pub.icon || "Folder",
    //                 visibility: "public"
    //             }))
    //     ];


    const sidebarProjectsMap = new Map();

    // ✅ Step 1: Add private projects
    (Array.isArray(projects) ? projects : []).forEach((p) => {
        sidebarProjectsMap.set(p.parent_scr_id, {
            ...p,
            visibility: "private",
        });
    });

    // ✅ Step 2: Add public projects (only if not already present)
    (publicProjects || []).forEach((pub) => {
        const exists = sidebarProjectsMap.has(pub.parent_scr_id);

        if (
            !exists &&
            publicWithChats.includes(pub.parent_scr_id)
        ) {
            sidebarProjectsMap.set(pub.parent_scr_id, {
                parent_scr_id: pub.parent_scr_id,
                category: pub.category,
                icon: pub.icon || "Folder",
                visibility: "public",
            });
        }
    });

    // ✅ Final array
    const sidebarProjects = Array.from(sidebarProjectsMap.values());


    return (
        <div className="flex flex-col w-full">
            {/* {projects.map((project) => { */}
            {/* {(Array.isArray(projects) ? projects : []).map((project) => { */}
            {sidebarProjects.map((project) => {
                const IconComponent = LucideIcons[project.icon] || LucideIcons.Folder;
                const chats = categoryChats[project.parent_scr_id] || [];
                return (
                    <div key={project.parent_scr_id} className="space-y-1"
                        onMouseEnter={() => setHoveredId(project.parent_scr_id)}
                        onMouseLeave={() => setHoveredId(null)}
                    >
                        {/* <div className="flex items-center gap-1 px-2 py-1 rounded-md dark:hover:bg-[#2a2a2a] hover:bg-zinc-200 group">
                            <IconComponent
                                className={`w-4 h-4 cursor-pointer`}
                                onClick={() => handleIconClick(project.parent_scr_id)}
                            />
                            {!isCollapsed && (
                                <>
                            {renamingId === project.parent_scr_id ? (
                                <input
                                    ref={inputRef}
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={handleRenameKey}
                                    className="w-full bg-transparent border-b border-zinc-600 focus:outline-none text-sm p-1"
                                    autoFocus
                                />
                            ) : (
                                <Button
                                    variant="ghost"
                                    className="gap-1 p-1 h-fit justify-start text-left flex items-center flex-1 min-w-0 hover:bg-transparent
    dark:hover:bg-transparent"
                                    onClick={() => {
                                        setOpenMobile?.(false);
                                        router.push(`/project/${project.parent_scr_id}`);
                                    }}
                                >
                                    <span className="flex-1 min-w-0 truncate">{capitalizeTitle(project.category)}</span>
                                </Button>
                            )}


                            <DropdownMenu
                                open={openMenuFor === project.parent_scr_id}
                                onOpenChange={(open) =>
                                    setOpenMenuFor(open ? project.parent_scr_id : null)
                                }>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        className={`p-1 rounded-md hover:bg-zinc-700 transition-opacity
                        ${hoveredId === project.parent_scr_id ||
                                                openMenuFor === project.parent_scr_id ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                    `}
                                    >
                                        <MoreHorizontalIcon size={16} />
                                    </button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent side="right" align="start">
                                    <DropdownMenuItem
                                        onClick={() => {
                                            setRenamingId(project.parent_scr_id);
                                            setNewName(project.category);
                                        }}
                                    >
                                        <PencilEditIcon />
                                        <span>Rename</span>
                                    </DropdownMenuItem>

                                    {project.visibility !== "public" && project.visibility !== "limited" && (
                                        <DropdownMenuItem
                                            className="text-destructive dark:text-red-500"
                                            onClick={() => handleDeleteProject(project.parent_scr_id)}
                                        >
                                            <TrashIcon />
                                            <span>Delete</span>
                                        </DropdownMenuItem>
                                    )}

                                </DropdownMenuContent>
                            </DropdownMenu>

                                </>
                            )}
                        </div> */}
                        <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-1"} px-2 py-1 rounded-md hover:bg-zinc-200 dark:hover:bg-[#2a2a2a]`} title={capitalizeTitle(project.category)}>

                            {/* ✅ ICON (always visible) */}
                            <IconComponent
                                className="w-4 h-4 cursor-pointer"
                                // onClick={() => handleIconClick(project.parent_scr_id)}
                                onClick={() => {
                                    if (isCollapsed) {
                                        // ✅ Direct navigation (NO dropdown)
                                        setOpenMobile?.(false);
                                        router.push(`/project/${project.parent_scr_id}`);
                                    } else {
                                        // ✅ Normal dropdown behavior
                                        handleIconClick(project.parent_scr_id);
                                    }
                                }}
                            />

                            {/* ✅ ONLY show when expanded */}
                            {!isCollapsed && (
                                <>
                            {renamingId === project.parent_scr_id ? (
                                        <input
                                            ref={inputRef}
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            onKeyDown={handleRenameKey}
                                            className="w-full bg-transparent border-b border-zinc-600 focus:outline-none text-sm p-1"
                                            autoFocus
                                        />
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            className="gap-1 p-1 h-fit justify-start flex-1 min-w-0 hover:bg-transparent"
                                            onClick={() => {
                                                router.push(`/project/${project.parent_scr_id}`);
                                            }}
                                        >
                                            <span className="truncate">
                                                {capitalizeTitle(project.category)}
                                            </span>
                                        </Button>
                                    )}

                                    {/* menu button */}
                                    <DropdownMenu
                                        open={openMenuFor === project.parent_scr_id}
                                        onOpenChange={(open) =>
                                            setOpenMenuFor(open ? project.parent_scr_id : null)
                                        }>
                                        <DropdownMenuTrigger asChild>
                                            <button
                                                className={`p-1 rounded-md hover:bg-zinc-700 transition-opacity
                        ${hoveredId === project.parent_scr_id ||
                                                        openMenuFor === project.parent_scr_id ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                    `}
                                            >
                                                <MoreHorizontalIcon size={16} />
                                            </button>
                                        </DropdownMenuTrigger>

                                        <DropdownMenuContent side="right" align="start">
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    setRenamingId(project.parent_scr_id);
                                                    setNewName(project.category);
                                                }}
                                            >
                                                <PencilEditIcon />
                                                <span>Rename</span>
                                            </DropdownMenuItem>

                                            {project.visibility !== "public" && project.visibility !== "limited" && (
                                                <DropdownMenuItem
                                                    className="text-destructive dark:text-red-500"
                                                    onClick={() => handleDeleteProject(project.parent_scr_id)}
                                                >
                                                    <TrashIcon />
                                                    <span>Delete</span>
                                                </DropdownMenuItem>
                                            )}

                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                        </div>

                        {!isCollapsed && openCategory === project.parent_scr_id && (
                            <div className="ml-6">
                                {chats.length === 0 && (
                                    <p className="ml-2 text-xs text-gray-500">No chats yet</p>
                                )}

                                {groupChatsByDate(chats).map(({ key, label, chats: groupedChats }) => (
                                    <div key={key} className="mb-2">
                                        <div className="pl-2 pr-2 py-1 text-xs text-sidebar-foreground/50">
                                            {label}
                                        </div>
                                        {groupedChats.map((chat) => (
                                            <div
                                                key={chat.screen_id}
                                                className="relative"
                                                onMouseEnter={() => setHoveredChatId(chat.screen_id)}
                                                onMouseLeave={() => setHoveredChatId(null)}
                                            >
                                                {renamingChatId === chat.screen_id ? (
                                                    <input
                                                        ref={chatInputRef}
                                                        contentEditable
                                                        suppressContentEditableWarning={true}
                                                        value={newChatName}
                                                        onInput={(e) => setNewChatName(e.target.value)}
                                                        onBlur={handleRenameChat}
                                                        onKeyDown={handleChatRenameKey}
                                                        className="w-full bg-transparent focus:outline-none text-sm p-1"
                                                    // autoFocus
                                                    />
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        className="w-full justify-start text-sm pr-8 pl-2"
                                                        onClick={() => {
                                                            // checkSubscriptionAndProceed(() => {
                                                            //     dispatch(setIsChatLoading(true));
                                                            //     setOpenMobile(false);
                                                            //     router.push(`/project/${project.parent_scr_id}/${chat.screen_id}`);
                                                            // })
                                                            dispatch(setIsChatLoading(true));
                                                            router.push(`/project/${project.parent_scr_id}/${chat.screen_id}`);
                                                        }}
                                                    >
                                                        {chat.title || "Untitled Chat"}
                                                    </Button>
                                                )}

                                                {/* MORE BUTTON */}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className={`absolute right-1 top-1 p-1 rounded-md transition hover:bg-zinc-700 ${hoveredChatId === chat.screen_id ? "opacity-100" : "opacity-0 pointer-events-none"
                                                                }`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <MoreHorizontalIcon size={16} />
                                                        </button>
                                                    </DropdownMenuTrigger>

                                                    {/* DROPDOWN CONTENT */}
                                                    <DropdownMenuContent side="bottom" align="end">

                                                        {activeTab == "Page Builder" && (
                                                            <DropdownMenuItem
                                                                className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
                                                                onClick={() => {
                                                                    dispatch(setActiveItem("z-[45]"));
                                                                    if (isGeneratingWebsite) {
                                                                        toast.error("Please hold on—we’re setting up your page.", {
                                                                            style: { color: "red" },
                                                                            position: "top-center",
                                                                            iconTheme: { primary: "red", secondary: "#fff" },
                                                                        });
                                                                        return;
                                                                    }
                                                                    setShareOpen(false);
                                                                    let generateData = {
                                                                        websiteUrl: chat.content,
                                                                        screenId: chat.screen_id,
                                                                        websiteDetailsUid: chat._id,
                                                                        userId: userId1,
                                                                    };
                                                                    console.log(generateData, "===generateData===generateData")
                                                                    setGenerateLinkData(generateData);
                                                                }}
                                                            >
                                                                <ShareIcon />
                                                                <span>Share Public Link</span>
                                                            </DropdownMenuItem>
                                                        )}
                                                        {chatType !== "chat" && (
                                                            <DropdownMenuItem
                                                                className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
                                                                onClick={() => {
                                                                    if (isFreeLimit) {
                                                                        // dispatch(setActiveItem("z-[45]"));
                                                                        setShowBuyCreditsModal(true);
                                                                        return;
                                                                    }
                                                                    if (activeTab === "Card Maker") {
                                                                        downloadCardFromURL(chat.content, chat.screen_id)
                                                                    } else if (activeTab === "Page Builder") {
                                                                        handleDownload(chat.content, chat.screen_id);
                                                                    } else {
                                                                        downloadPDF(chat.content, chat.screen_id)
                                                                    }
                                                                }}
                                                            >
                                                                <DownloadIcon />
                                                                <span>Download</span>
                                                            </DropdownMenuItem>
                                                        )}
                                                        {activeTab == "Page Builder" && (
                                                            <DropdownMenuItem
                                                                className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
                                                                onClick={() => {
                                                                    if (isFreeLimit) {
                                                                        setShowBuyCreditsModal(true);
                                                                        return;
                                                                    }
                                                                    setOpenMobile(false);
                                                                    handleView(chat.content);
                                                                }}
                                                            >
                                                                <EyeIcon />
                                                                <span>Preview</span>
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            onClick={() => {
                                                                setRenamingChatId(chat.screen_id);
                                                                setNewChatName(chat.title);
                                                            }}
                                                        >
                                                            <PencilEditIcon />
                                                            <span>Rename</span>
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
            {generateLinkData && shareOpen && userData && (
                console.log('===here=='),
                <ShareLinkPopup
                    open={shareOpen}
                    onClose={() => setShareOpen(false)}
                    generateData={generateLinkData}
                    isWebsiteHistoryPage={true}
                    userDetail={userData}
                />
            )}

            {showBuyCreditsModal && userData && (
                <BuyCreditsModal
                    show={showBuyCreditsModal}
                    handleClose={() => setShowBuyCreditsModal(false)}
                    userDetail={userData}
                />
            )}
            {showPlan && userData && (
                <UpgradePlan
                    show={showPlan}
                    handleClose={() => setShowPlan(false)}
                    userDetail={userData}
                />
            )}
        </div>
    );
}
